import React, { useCallback, useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { borderRadius } from '../../../theme/theme';
import { useMappingConfigurationContext } from '../ui/MappingConfigurationContext';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { useSelectedOption } from '../../../shared/stores/useUIStore';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { useAuthStore } from '../../../shared/stores/useAuthStore';
import { useThemeContext } from '../../../shared/providers/ThemeContext';
import { buildMappingWorkspaceTourSteps } from '../lib/mappingWorkspaceGuideContent';
import { getActiveValidationTargetPages, shouldShowWorkspaceValidationNotice } from '../lib/mappingWorkspacePageModel';
import { useMappingWorkspaceOrchestration } from './useMappingWorkspaceOrchestration';
import { useMappingScopeEditorPersistence } from './useMappingScopeEditorPersistence';
import { useQuickFillEmbeddedLayoutTrace } from './useQuickFillEmbeddedLayoutTrace';
import { useObjectTypeManagement } from './useObjectTypeManagement';
import { useMappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';
import { useWorkspaceDerivedModels } from './useWorkspaceDerivedModels';
import { useMappingWorkspaceNavigationController } from './useMappingWorkspaceNavigationController';
import { useMappingWorkspaceProfileRuntime } from './useMappingWorkspaceProfileRuntime';
import { useMappingWorkspaceChromeRuntime } from './useMappingWorkspaceChromeRuntime';
import { useMappingModelDataRefresh } from './useMappingModelDataRefresh';
import type { MappingConfigurationPageContentProps, MappingWorkspaceContentModel } from './mappingWorkspaceContentModel.types';
export function useMappingWorkspaceContentModel(
  props: MappingConfigurationPageContentProps
): MappingWorkspaceContentModel {
  const {
    onGoBack,
    isSaving,
    saveError,
    handleApply,
    handleContinue,
    objectTypeOptions,
    setSaveError,
    selectObjectType,
    workflowSetupCompleted,
    projectPreferredProfile,
    projectIdentityPending,
    onReinitiateWorkflow,
    profiles,
    profilesLoading,
    selectedProfileKey,
    profileError,
    selectedSettingsType,
    onSelectedSettingsTypeChange,
    onRefreshProfiles,
    onLoadProfile,
    onSaveProfile,
    onSaveProfileAs,
    onRenameProfile,
    onDeleteProfile, onDeleteProfiles,
  } = props;

  const theme = useTheme();
  const themeContext = useThemeContext();
  const selectedOption = useSelectedOption();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const [hostProfileSaveAsDialogOpen, setHostProfileSaveAsDialogOpen] = React.useState(false);
  const [hostProfileRenameDialogProfileKey, setHostProfileRenameDialogProfileKey] = React.useState<string | null>(null);
  const handleSignOut = React.useCallback(async () => {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    try {
      await logout();
    } finally {
      setIsSigningOut(false);
    }
  }, [isSigningOut, logout]);
  const setFocusedSystem = usePcfValidationStore((s) => s.setFocusedSystem);
  const requestedWorkspaceTab = usePcfValidationStore((s) => s.requestedWorkspaceTab);
  const requestWorkspaceTab = usePcfValidationStore((s) => s.requestWorkspaceTab);
  const clearRequestedWorkspaceTab = useCallback(
    () => requestWorkspaceTab(null),
    [requestWorkspaceTab]
  );
  const issueNavigationContext = usePcfValidationStore((s) => s.issueNavigationContext);
  const isMobile = useMediaQuery('(max-width:1100px), (max-height:640px)');
  const activeDocumentInfo = useActiveDocumentInfoContext();
  const showMappingInfo = useSnackbarStore((state) => state.showInfo);

  const { focusRefs, setQuickFillWorkspaceShellRef } = useMappingWorkspaceFocusRefs();

  const {
    processConditionsEditorState,
    handleProcessConditionsEditorStateChange,
    projectPropertiesEditorState,
    handleProjectPropertiesEditorStateChange,
  } = useMappingScopeEditorPersistence();

  const {
    selectedObjectType,
    objectTypeOptions: contextObjectTypes,
    advancedSettings,
  } = useMappingConfigurationContext();
  const menuConfig = useConfigurationStore((state) => state.menuConfiguration);
  const connectorConfig = useConfigurationStore((state) => state.connectorBindingConfiguration);
  const processConfig = useConfigurationStore((state) => state.processPropertiesConfiguration);
  const boltConfig = useConfigurationStore((state) => state.boltConfiguration);
  const categoryConfig = useConfigurationStore((state) => state.additionalCategoriesConfiguration);
  const currentSettingsNeedProfileSave = useConfigurationStore((state) => state.isDirty);
  const workflowApplied = useConfigurationStore((state) => state.isWorkflowApplied);
  const quickFillHasPendingChanges = useQuickFillStore((state) => state.hasPendingChanges);


  const {
    scopeSummaries,
    sectionSummaries,
    workspaceNavigatorSummary,
    workspaceSummary,
    workspaceReady,
    workspaceSummaryText,
    showCompletedWorkflowChrome,
    showMappingWorkspaceSummary,
    workflowSummaryChips,
    workflowSummaryCards,
    workspaceGuides,
    selectedProfileName,
  } = useWorkspaceDerivedModels({
    contextObjectTypes,
    advancedCenterOfCoordinates: advancedSettings.centerOfCoordinates,
    workflowSetupCompleted,
    selectedOption,
    selectedSettingsType,
    selectedProfileKey,
  });


  const profileRuntime = useMappingWorkspaceProfileRuntime({
    props,
    activeDocumentInfo,
    objectTypes: contextObjectTypes,
    menuConfig,
    connectorConfig,
    processConfig,
    boltConfig,
    categoryConfig,
    processConditionsEditorState,
    projectPropertiesEditorState,
    focusRefs,
    showMappingInfo,
  });
  const {
    gate,
    repair,
    profileLoad,
    guarded,
    showProjectProfileVerifiedBanner,
    progress,
  } = profileRuntime;
  const {
    resolvedProfileName,
    restorePersistedWorkspace,
    resumeScopeKey,
    quickFillProfileRestorePending,
    projectProfileGateOpen,
    projectProfileGateBlocksActions,
    projectProfileGateProfileKey,
    projectProfileGateDecision,
    profileProjectMatches,
    handleProjectProfileGateSelectedProfileKeyChange,
  } = gate;
  const {
    mappingReadiness,
    handleRepairSetupConfirm,
    repairConfirmationDialog,
    grantRepairConsent,
    handleProjectProfileGateConfirmWithRepairConsent,
  } = repair;
  const { modelDataStale, handleRefreshModelData } = useMappingModelDataRefresh({
    refreshReadiness: mappingReadiness.refresh,
  });
  const { profileLoadConfirmationDialog } = profileLoad;
  const { handleGuardedProfileChange, handleGuardedProfileLoad } = guarded;
  const {
    workspaceBootLoading,
    workspaceLoadingModel,
    quickFillProfileRestoreProgressModel,
    profileApplyWindowOpen,
    profileApplyWindowModel,
    handleProfileApplyProgressClose,
  } = progress;

  const firstStatusCategoryId = useConfigurationStore(
    (state) => state.statusBuilderConfiguration?.categories?.[0]?.id ?? null
  );

  const mappingTourSteps = useMemo(
    () =>
      buildMappingWorkspaceTourSteps({
        refs: focusRefs,
        isMobile,
        objectTypeTargetName: selectedObjectType,
        firstStatusCategoryId,
      }),
    [firstStatusCategoryId, focusRefs, isMobile, selectedObjectType]
  );
  const {
    activeTab,
    setActiveTab,
    selectedNode,
    handleNodeSelect,
    isTreeCollapsed,
    setIsTreeCollapsed,
    mobileMenuOpen,
    setMobileMenuOpen,
    summaryWindowOpen,
    mappingGuideOpen,
    setMappingGuideOpen,
    mappingTourOpen,
    mappingTourStep,
    setMappingTourStep,
    handleCloseMappingTour,
    handleOpenMappingTour,
    handleCloseSummaryWindow,
    handleOpenSummaryWindow,
    closeWorkspaceOverlays,
    handleOpenActivePageGuide,
    handleOpenActivePageTour,
  } = useMappingWorkspaceOrchestration({
    showMappingWorkspaceSummary,
    objectTypeOptions,
    onSelectObjectType: selectObjectType,
    requestedWorkspaceTab,
    issueNavigationContext,
    openQuickFillGuide: () => focusRefs.quickFillActions.current?.openGuide(),
    openQuickFillTour: () => focusRefs.quickFillActions.current?.openTour?.(),
    openValidationGuide: () => focusRefs.validationGuideActions.current?.openGuide(),
    openValidationTour: () => focusRefs.validationGuideActions.current?.openTour?.(),
    openExportGuide: () => focusRefs.exportGuideActions.current?.openGuide(),
    openExportTour: () => focusRefs.exportGuideActions.current?.openTour?.(),
    mappingTourSteps,
    resumeScopeKey,
    restorePersistedWorkspace,
  });
  const quickFillProfileRestoreWindowOpen =
    activeTab === 1 &&
    quickFillProfileRestorePending &&
    !projectProfileGateOpen;
  const activeValidationTargets = useMemo(
    () => getActiveValidationTargetPages(activeTab),
    [activeTab]
  );
  const { handleAddObjectType, handleDeleteObjectType } = useObjectTypeManagement({
    handleNodeSelect,
    objectTypeOptions,
    selectedNodeName: selectedNode.name,
  });

  // Once Apply/Save marks the workflow applied, Back would drop the user on
  // wizard pages whose shell steps are already torn down — Reinitiate is the
  // way back. Back shows only on the Mapping tab; the page chrome hides it on
  // the embedded Quick Fill, Validation, and Export tabs.
  const showMappingWorkspaceBackButton =
    activeTab !== 0 || (!summaryWindowOpen && !workflowApplied);

  const { isQuickFillWorkspaceTab } = useQuickFillEmbeddedLayoutTrace({
    activeTab,
    focusRefs,
  });

  const {
    handleWorkspaceApply,
    handleWorkspaceContinue,
    requestWorkspaceTabChange,
    handleMappingBack,
    handleReturnToGuidedSetup,
    unsavedTabChangeDialog,
    handleSelectWorkspaceTab,
    handleOpenQuickFill,
    handleOpenValidationForSystem,
  } = useMappingWorkspaceNavigationController({
    focusRefs,
    isQuickFillWorkspaceTab,
    activeTab,
    setActiveTab,
    requestedWorkspaceTab,
    clearRequestedWorkspaceTab,
    activeDocumentInfo,
    projectIdentityPending,
    projectProfileGateBlocksActions,
    mappingReadiness,
    quickFillHasPendingChanges,
    currentSettingsNeedProfileSave: Boolean(currentSettingsNeedProfileSave),
    setSaveError,
    handleApply,
    handleContinue,
    onLoadProfile,
    closeWorkspaceOverlays,
    onGoBack,
    setFocusedSystem,
  });

  const {
    profileActionsDisabled,
    profileSelectionDisabled,
    profileLoadDisabled,
    workspaceApplyDisabled,
    workspaceContinueDisabled,
    workspaceApplyNeedsAttention,
  } = useMappingWorkspaceChromeRuntime({
    props,
    profileRuntime,
    processConfig,
    activeDocumentInfo,
    focusRefs,
    activeTab,
    isQuickFillWorkspaceTab,
    requestWorkspaceTabChange,
    currentSettingsNeedProfileSave: Boolean(currentSettingsNeedProfileSave),
    quickFillHasPendingChanges,
    showMappingWorkspaceSummary,
    user: user ? { username: user.username } : null,
    isSigningOut,
    handleOpenActivePageGuide,
    handleOpenActivePageTour,
    handleOpenSummaryWindow,
    handleSignOut,
    themeContext,
    setHostProfileSaveAsDialogOpen,
    setHostProfileRenameDialogProfileKey,
  });

  const exportActionsDisabled =
    projectIdentityPending || projectProfileGateBlocksActions || mappingReadiness.isBlocking;
  const exportActionsDisabledReason = projectIdentityPending
    ? 'Waiting for the active Revit project before exporting PCF files.'
    : projectProfileGateBlocksActions
      ? 'Verify or load a mapping profile for this Revit project before exporting PCF files.'
      : mappingReadiness.message || 'Active project mapping setup is not ready for PCF export yet.';
  const compactStatusAlertSx = {
    mx: 0,
    mb: 0,
    borderRadius: `${borderRadius.none}px`,
    py: 0,
    px: 0.75,
    minHeight: 30,
    borderLeft: 'none',
    borderRight: 'none',
    '& .MuiAlert-icon': {
      py: 0.35,
      mr: 0.75,
      fontSize: '0.9rem',
    },
    '& .MuiAlert-message': {
      py: 0.35,
    },
  } as const;

  return {
    focusRefs,
    setQuickFillWorkspaceShellRef,
    activeDocumentInfo,
    theme,
    isMobile,
    saveError,
    setSaveError,
    profileError,
    projectIdentityPending,
    profiles,
    profilesLoading,
    selectedProfileKey,
    selectedSettingsType,
    selectedProfileName,
    resolvedProfileName,
    isSaving,
    currentSettingsNeedProfileSave,
    profileActionsDisabled,
    profileSelectionDisabled,
    profileLoadDisabled,
    hostProfileSaveAsDialogOpen,
    setHostProfileSaveAsDialogOpen,
    hostProfileRenameDialogProfileKey,
    setHostProfileRenameDialogProfileKey,
    activeTab,
    handleSelectWorkspaceTab,
    handleMappingBack,
    unsavedTabChangeDialog,
    user: user ? { username: user.username } : null,
    isSigningOut,
    handleSignOut,
    showMappingWorkspaceBackButton,
    compactStatusAlertSx,
    handleWorkspaceApply,
    handleWorkspaceContinue,
    workspaceApplyDisabled,
    workspaceContinueDisabled,
    workspaceApplyNeedsAttention,
    onReinitiateWorkflow,
    showProjectProfileVerifiedBanner,
    mappingReadiness,
    modelDataStale,
    handleRefreshModelData,
    handleRepairSetupConfirm,
    grantRepairConsent,
    handleOpenQuickFill,
    repairConfirmationDialog,
    profileLoadConfirmationDialog,
    projectProfileGateOpen,
    projectProfileGateBlocksActions,
    projectProfileGateProfileKey,
    handleProjectProfileGateSelectedProfileKeyChange,
    handleProjectProfileGateConfirm: handleProjectProfileGateConfirmWithRepairConsent,
    projectPreferredProfile,
    profileProjectMatches,
    projectProfileGateDecision,
    // The workspace controls get the guarded wrappers; the raw gate handlers
    // stay private so nothing can select or load a profile past the guard.
    handleWorkspaceSelectedProfileChange: handleGuardedProfileChange,
    onSelectedSettingsTypeChange,
    handleLoadProfileForCurrentProject: handleGuardedProfileLoad,
    onRefreshProfiles,
    onSaveProfile,
    onSaveProfileAs,
    onRenameProfile,
    onDeleteProfile, onDeleteProfiles,
    quickFillProfileRestoreWindowOpen,
    quickFillProfileRestoreProgressModel,
    profileApplyWindowOpen,
    profileApplyWindowModel,
    handleProfileApplyProgressClose,
    exportActionsDisabled,
    exportActionsDisabledReason,
    activeValidationTargets,
    shouldShowWorkspaceValidationNotice,
    contextObjectTypes,
    scopeSummaries,
    sectionSummaries,
    workspaceNavigatorSummary,
    highlightedAttributeNames:
      issueNavigationContext?.targetPage === 'mapping'
        ? issueNavigationContext.actionParams?.highlightedAttributes ||
          issueNavigationContext.affectedItems
        : [],
    processConditionsEditorState,
    handleProcessConditionsEditorStateChange,
    projectPropertiesEditorState,
    handleProjectPropertiesEditorStateChange,
    showMappingWorkspaceSummary,
    summaryWindowOpen,
    quickFillProfileRestorePending,
    showCompletedWorkflowChrome,
    workflowSummaryChips,
    workflowSummaryCards,
    workspaceGuides,
    mappingTourSteps,
    mappingGuideOpen,
    setMappingGuideOpen,
    mappingTourOpen,
    mappingTourStep,
    setMappingTourStep,
    handleCloseMappingTour,
    handleOpenMappingTour,
    handleCloseSummaryWindow,
    handleOpenSummaryWindow,
    handleReturnToGuidedSetup,
    handleOpenActivePageGuide,
    handleOpenActivePageTour,
    selectedNode,
    handleNodeSelect,
    isTreeCollapsed,
    setIsTreeCollapsed,
    mobileMenuOpen,
    setMobileMenuOpen,
    handleAddObjectType,
    handleDeleteObjectType,
    handleOpenValidationForSystem,
    workspaceSummary,
    workspaceReady,
    workspaceSummaryText,
    resumeScopeKey,
    workspaceBootLoading,
    workspaceLoadingModel,
  };
}
